import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiCalendar } from 'react-icons/hi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchUserStats } from '../../services/userService';
import tabCache from '../../utils/tabCache';

const Dashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const datePickerRef = useRef(null);
  const prevEndDate = useRef(null);
  const [chartData, setChartData] = useState(tabCache.dashboard.data || []);
  const [totalUsers, setTotalUsers] = useState(
    tabCache.dashboard.totals?.users || 0
  );
  const [totalFeedback, setTotalFeedback] = useState(
    tabCache.dashboard.totals?.feedback || 0
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchParams = useRef(null);

  // Format date for display
  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${startDate.toISOString().split('T')[0]} → ${endDate.toISOString().split('T')[0]}`;
    }
    return 'YYYY-MM-DD → YYYY-MM-DD';
  };

  // Format date for URL parameters
  const formatDateForUrl = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Navigate with date filters
  const navigateWithDateFilter = (path) => {
    if (startDate && endDate) {
      const startDateStr = formatDateForUrl(startDate);
      const endDateStr = formatDateForUrl(endDate);
      navigate(`${path}?startDate=${startDateStr}&endDate=${endDateStr}`);
    } else {
      navigate(path);
    }
  };

  // Preset handlers
  const handlePreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'all':
        // Clear date selection when 'All' is selected
        start = null;
        end = null;
        break;
      case 'today':
        start = new Date(today);
        end = new Date(today);
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        break;
      case 'last7days':
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        end = new Date(today);
        break;
      case 'last30days':
        start = new Date(today);
        start.setDate(start.getDate() - 30);
        end = new Date(today);
        break;
      case 'thismonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today);
        break;
      case 'lastmonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }
    setDateRange([start, end]);
    setSelectedPreset(preset);
    setShowDatePicker(false);
  };

  const hasFetchedOnce = useRef(false);





  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Close date picker when both start and end dates are selected (only when range is newly completed)
  useEffect(() => {
    // Only close if endDate was just set (changed from null/undefined to a date)
    const wasEndDateNull = !prevEndDate.current;
    const isEndDateSet = !!endDate;

    if (startDate && endDate && wasEndDateNull && isEndDateSet && showDatePicker) {
      // Add a small delay to ensure the selection is complete
      const timer = setTimeout(() => {
        setShowDatePicker(false);
      }, 150);
      prevEndDate.current = endDate;
      return () => clearTimeout(timer);
    }

    // Update the ref to track current endDate
    prevEndDate.current = endDate;
  }, [startDate, endDate, showDatePicker]);

  // Fetch user stats when date range changes
  useEffect(() => {
    if (!hasFetchedOnce.current) {
      hasFetchedOnce.current = true;
    } else if (!startDate && !endDate) {
     
      return;
    }
    const isFirstLoad = tabCache.dashboard.firstLoad;

    // Format dates as YYYY-MM-DD
    const formatDate = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Handle date range - if "all" is selected (both null), use wide range
    // Otherwise use selected dates or default to today
    // Build proper date range for API
    let startDateStr = null;
    let endDateStr = null;

    const getDayRange = (date) => {
      const start = new Date(date);
      start.setHours(0, 0, 0, 0);

      const end = new Date(date);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    };

    // If user selected a range
    if (startDate && endDate) {
      const startRange = getDayRange(startDate);
      const endRange = getDayRange(endDate);

      startDateStr = startRange.start.toISOString();
      endDateStr = endRange.end.toISOString();
    }
    // If "All" selected → keep both NULL (no filter)

    const fetchKey = `${startDateStr}-${endDateStr}`;

    // Prevent duplicate calls with same parameters (but allow if cache is stale)
    if (lastFetchParams.current === fetchKey && !isFirstLoad) {
      // If we have cached data for this key, use it
      if (tabCache.dashboard.totals && tabCache.dashboard.totals.users !== undefined) {
        setTotalUsers(tabCache.dashboard.totals.users);
        setTotalFeedback(tabCache.dashboard.totals.feedback || 0);
        setChartData(tabCache.dashboard.data || []);
        return;
      }
    }

    lastFetchParams.current = fetchKey;

    const loadUserStats = async () => {
      if (isFirstLoad) setLoading(true);
      setError(null);

      try {
        const response = await fetchUserStats(startDateStr, endDateStr);

        // fetchUserStats returns response.data
        const resData = response?.data || response || {};

        // 1. Extract Chart Data
        let dataArray = resData.dayWise || resData.monthWise || [];
        if (!Array.isArray(dataArray) && Array.isArray(resData)) {
          dataArray = resData;
        }

        const formattedChartData = dataArray.map((item, index) => ({
          date: item.date || item._id || item.timestamp || `date-${index}`,
          users: Number(item.count || item.users || 0)
        })).sort((a, b) => new Date(a.date) - new Date(b.date));

        // 2. Calculate Totals based on filter
        const isAllSelected = !startDate && !endDate;

        // Use filtered fields if range is selected, otherwise use total fields
        const finalUsers = isAllSelected
          ? (resData.totalUsers ?? 0)
          : (resData.filteredUsers ?? 0);

        const finalFeedback = isAllSelected
          ? (resData.totalFeedback ?? 0)
          : (resData.filteredFeedback ?? 0);

        // Update State
        setChartData(formattedChartData);
        setTotalUsers(Number(finalUsers));
        setTotalFeedback(Number(finalFeedback));

        // Update Cache
        tabCache.dashboard.data = formattedChartData;
        tabCache.dashboard.totals = {
          users: Number(finalUsers),
          feedback: Number(finalFeedback)
        };
        tabCache.dashboard.firstLoad = false;

      } catch (err) {
        // console.error('Dashboard Fetch Error:', err);
        setError('Failed to load statistics');
        setChartData([]);
        setTotalUsers(0);
        setTotalFeedback(0);
      } finally {
        if (isFirstLoad) setLoading(false);
      }
    };

    loadUserStats();
  }, [startDate, endDate]);

  // Calculate date range for adaptive formatting
  const getDateRangeDays = () => {
    if (!startDate || !endDate) {
      // Default to full data range if no date selected
      if (chartData.length > 0) {
        const firstDate = new Date(chartData[0].date);
        const lastDate = new Date(chartData[chartData.length - 1].date);
        return Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24));
      }
      return 30;
    }
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  // Format date for X-axis - always show date and month
  const formatXAxisTick = (value) => {
    const date = new Date(value);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${monthNames[date.getMonth()]}`;
  };

  // Format date for tooltip
  const formatTooltipDate = (dateString) => {
    const date = new Date(dateString);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Format Y-axis tick
  const formatYAxisTick = (value) => {
    if (value === 0) return '0';
    if (value < 1000) return value.toString();
    return `${(value / 1000).toFixed(0)}k`;
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-brand/30 rounded-lg p-3 shadow-xl">
          <p className="text-gray-800 text-sm font-medium mb-1">
            {formatTooltipDate(payload[0].payload.date)}
          </p>
          <p className="text-brand text-sm font-semibold">
            Total Users: {payload[0].value.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };


  

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar relative">
          {/* Header - Always Visible */}
          <div className="mb-4 md:mb-6 relative z-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors relative z-10"
                >
                  <HiMenu className="w-6 h-6 text-gray-800" />
                </button>
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">Dashboard</h1>
              </div>

              {/* Date Range Picker */}
              <div className="relative md:self-auto self-start z-10" ref={datePickerRef}>
                <button
                  onClick={() => {
                    // When opening picker, set prevEndDate to current endDate so it doesn't auto-close
                    if (!showDatePicker && startDate && endDate) {
                      prevEndDate.current = endDate;
                    }
                    setShowDatePicker(!showDatePicker);
                  }}
                  className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-lg transition-colors shadow-md border border-gray-200"
                >
                  <span className="text-sm font-medium">{formatDateRange()}</span>
                  <HiCalendar className="w-4 h-4 text-gray-600" />
                </button>

                {/* Date Picker Dropdown */}
                {showDatePicker && (
                  <div className="absolute left-0 sm:right-0 sm:left-auto top-full mt-2 bg-white rounded-lg shadow-md border border-gray-200 p-4 z-[120] w-[290px] max-w-[95vw] sm:w-auto sm:min-w-[480px]">
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-0">
                      {/* Left Panel - Presets */}
                      <div className="w-full sm:w-1/3 sm:border-r sm:border-gray-200 sm:pr-4 sm:pl-0">
                        {/* <h3 className="text-gray-900 text-sm font-semibold mb-3">Quick Select</h3> */}
                        <div className="space-y-1 ml-2 sm:ml-0">
                          {[
                            { key: 'all', label: 'All' },
                            { key: 'today', label: 'Today' },
                            { key: 'yesterday', label: 'Yesterday' },
                            { key: 'last7days', label: 'Last 7 days' },
                            { key: 'last30days', label: 'Last 30 days' },
                            { key: 'thismonth', label: 'This month' },
                            { key: 'lastmonth', label: 'Last month' },
                          ].map((preset) => (
                            <button
                              key={preset.key}
                              onClick={() => handlePreset(preset.key)}
                              className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${selectedPreset === preset.key
                                ? 'bg-[#BC6ED7]/20 text-[#BC6ED7] font-semibold'
                                : 'text-gray-900 hover:bg-[#BC6ED7]/10 hover:text-[#BC6ED7]'
                                }`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Right Panel - Calendar */}
                      <div className={`flex-1 ${startDate && endDate ? 'dates-selected' : ''} sm:pl-4`}>
                        <DatePicker
                          selected={startDate}
                          onChange={(update) => {
                            setDateRange(update);
                            setSelectedPreset(null);
                          }}
                          startDate={startDate}
                          endDate={endDate}
                          selectsRange
                          inline
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Loader Overlay - Covers content area */}
          {loading ? (
            <div className="absolute inset-0 z-40 flex items-center justify-center bg-primary">
              <div className="flex items-center gap-3" role="status" aria-live="polite">
                <svg
                  className="w-12 h-12 animate-spin"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#E9D5FF" />
                      <stop offset="25%" stopColor="#C084FC" />
                      <stop offset="50%" stopColor="#A855F7" />
                      <stop offset="75%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#6B21A8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="url(#purple-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="60 200"
                    fill="none"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
          ) : null}

          {/* Stats Cards Section - First Row */}
          {!loading && (
            <div className=" mt-2 sm:mt-8 ">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
                {/* Total Users Card */}
                <div className="w-full">
                  <div
                    onClick={() => navigateWithDateFilter('/users')}
                    className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 transition-all cursor-pointer "
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-brand/20 p-3 rounded-xl transition-all duration-300">
                        <svg
                          className="w-6 h-6 text-brand"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm font-medium mb-1">Total Users</p>
                        {error ? (
                          <p className="text-red-600 text-sm font-medium">{error}</p>
                        ) : (
                          <p className="text-gray-800 text-2xl font-bold">{totalUsers.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feedback Card */}
                <div className="w-full">
                  <div
                    onClick={() => navigateWithDateFilter('/feedback')}
                    className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 transition-all cursor-pointer "
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-brand/20 p-3 rounded-xl transition-all duration-300">
                        <svg
                          className="w-6 h-6 text-brand"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm font-medium mb-1">Total Feedback</p>
                        <p className="text-gray-800 text-2xl font-bold">{totalFeedback.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Chart Section - Second Row */}
          {!loading && (
            <div className="mt-4 lg:mt-6">
              <div className="bg-white rounded-2xl p-4 shadow-xl border border-gray-200">
                <h3 className="text-gray-800 text-lg font-semibold mb-6">Users Growth</h3>

                {error ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <p className="text-red-600">{error}</p>
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <p className="text-gray-600">No data available for the selected date range</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
                      <defs>
                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#58327E" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#58327E" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} />
                      <XAxis
                        dataKey="date"
                        stroke="#6B7280"
                        style={{ fontSize: '11px', fill: '#6B7280' }}
                        tickFormatter={formatXAxisTick}
                        label={{ value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#6B7280', fontSize: '12px', fontWeight: 500 } }}
                        tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                        axisLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                      />
                      <YAxis
                        stroke="#6B7280"
                        style={{ fontSize: '11px', fill: '#6B7280' }}
                        tickFormatter={formatYAxisTick}
                        domain={[0, 'auto']}
                        label={{ value: 'Total Users', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6B7280', fontSize: '12px', fontWeight: 500 } }}
                        tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                        axisLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="users"
                        stroke="#58327E"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorUsers)"
                        dot={false}
                        activeDot={{ r: 4, fill: '#58327E', stroke: 'none', strokeWidth: 0 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

